import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, App, Button, Card, Col, Descriptions, Divider, Empty, Flex, Form, Input, Modal, Row, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePlanOptions } from '@/shared/api/reference'
import { displayName } from '@/shared/lib/planName'
import { Can } from '@/shared/auth/Can'
import { useAuth } from '@/shared/auth/useAuth'
import { formatDateTime, getCurrencyCode, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue, money, type Money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { AgentGovernance } from '@/features/agent/AgentGovernance'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  applyLocalList,
  byInstant,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { useIdempotencyKey } from '@/shared/lib/useIdempotencyKey'
import { AgentCreateFields } from '@/features/agent/AgentCreateFields'
import { ResellerCreateFields } from '@/features/agent/ResellerCreateFields'
import {
  createAgentAccountWithCommission,
  type AccountStep,
} from '@/features/agent/agentAccountApi'
import { CommissionFields } from '@/features/agent/CommissionFields'
import {
  COMMISSION_OPTIONAL_INITIAL,
  isCommissionRequested,
  toCommissionRuleWithoutAgent,
  useCommissionState,
  type CommissionFormValues,
} from '@/features/agent/commissionForm'
import {
  AGENT_STATES,
  FUND_LIMIT_KINDS,
  WINDOWED_FUND_LIMIT_KINDS,
  fetchAgentBalance,
  fetchAgentChildren,
  fetchAgentRoots,
  closeCommissionRule,
  fetchCommissionRules,
  fetchFundLimits,
  searchAgents,
  setDebtLimit,
  setFundLimits,
  topUpAgent,
  type AgentKind,
  type AgentNode,
  type CommissionRule,
  type SettlementMode,
} from '@/features/agent/agentApi'
import { agentGrantsHref, agentTransactionsHref } from '@/features/agent/agentLinks'
import { AgentEditModal } from '@/features/agent/AgentEditModal'
import { AgentMoveModal } from '@/features/agent/AgentMoveModal'
import { CommissionRuleModal } from '@/features/agent/CommissionRuleModal'
import { defaultPerTxnLimit } from '@/features/agent/commissionTiers'

/**
 * Agentda AMALDAGI UMUMIY komissiya qoidasi bormi.
 *
 * ⚠️ Paketga bog'langan qoida (`planId !== null`) hisobga OLINMAYDI:
 * komissiya faqat balans to'ldirishda hisoblanadi, to'ldirishda esa
 * paket tushunchasi yo'q (SHARTNOMA §1). Ya'ni faqat umumiy qoida
 * haqiqatan cashback beradi.
 */
const isGeneralOpenRule = (rule: CommissionRule) => rule.validTo === null && rule.planId === null

/**
 * Komissiya OLADIGAN turlar — 2026-08-21 dan beri IKKALASI ham
 * (egasi: «resellerda agentda bor barcha funksiya bo'ladi»).
 *
 * ⚠️ Ro'yxat ATAYLAB qo'lda sanalgan, `agentApi` dagi umumiy ro'yxatdan
 * OLINMAGAN: XSB-002 qorovuli (`tests/tester-bugs.mjs`) uni shu sahifaga
 * import qilishni taqiqlaydi — o'sha import yaratish formasiga sizib,
 * «Resellerlar» da agent yaratish yo'lini qayta ochardi.
 *
 * ⚠️ Bu FAQAT ko'rinish darvozasi. Pul o'zi turga umuman qaramaydi —
 * `DepositServiceImpl#accrueCommission` har to'ldirishdan keyin shartsiz
 * chaqiriladi va qoida topilmasa nol qaytaradi
 * (`CommissionServiceImpl#accrue`). Ya'ni bu yerdagi kengayish
 * operatorga QOIDA QO'YISH yo'lini ochadi, o'zi pul yozmaydi.
 */
const COMMISSION_EARNING_KINDS: readonly AgentKind[] = ['AGENT', 'RESELLER']

/** Yaratish formasida komissiya maydonlari SHU tugun ostida yashaydi. */
const COMMISSION_PREFIX = ['commission'] as const

/**
 * «Oxirgi qo'shilgan doim birinchi» (egasi, 2026-08-20).
 *
 * ⚠️ Modul darajasida — `useLocalListState` uni maydonlari bo'yicha
 * bog'laydi, lekin har chizishda yangi obyekt yasash keraksiz qayta
 * hisoblash bo'lardi.
 *
 * ⚠️ Vaqti noma'lum tugun ro'yxat OXIRIDA qoladi (`byInstant`
 * izohi) — «yangi qo'shilgan» bo'lib boshini egallamaydi.
 */
const DEFAULT_AGENT_SORT = { field: 'createdAt', order: 'descend' } as const

const KIND_COLOR: Record<AgentKind, string> = {
  AGENT: 'blue',
  RESELLER: 'purple',
}

/**
 * Shu marshrutda qaysi turlar ko'rinadi.
 *
 * ⚠️ Turlar KESISHMAYDI: `BOTH` olib tashlangandan keyin (2026-08-21)
 * har tugun aniq bitta ro'yxatda turadi. Ilgari `BOTH` ikkalasida ham
 * chiqardi — bu shart endi yo'q.
 */
const VISIBLE_KINDS: Record<'AGENT' | 'RESELLER', readonly AgentKind[]> = {
  AGENT: ['AGENT'],
  RESELLER: ['RESELLER'],
}

/**
 * Yaratish formasining qiymatlari — IKKI FORMA uchun BITTA shakl.
 *
 * ⚠️ Ko'p maydon ixtiyoriy va bu ataylab: tur MARSHRUTDAN keladi va
 * formada umuman chizilmaydi, huquqsiz operatorda esa
 * `username`/`rawPassword` va pul maydonlari chizilmaydi. `preserve={false}`
 * bilan chizilmagan maydon formadan tushib qoladi, ya'ni ularni majburiy
 * deb e'lon qilish YOLG'ON tip bo'lardi.
 */
interface CreateValues {
  code: string
  displayName: string
  /**
   * «Pul ham qabul qiladigan sotuvchi» — FAQAT reseller formasida.
   *
   * ⚠️ Ilgari bu `kind === 'BOTH'` turi edi. Tur olib tashlangach
   * (2026-08-21) imkoniyat YO'QOLMADI — u `identity.app_user.cash_desk`
   * bayrog'iga ko'chdi, backend uni `AgentOnboardRequest.cashDesk` da
   * ANCHADAN BERI qabul qiladi. Ya'ni bu belgilagich yangi huquq
   * ochmaydi, eski yo'lni ochiq tutadi.
   */
  cashDesk?: boolean
  settlementMode: SettlementMode
  parentId?: string | null
  username?: string
  rawPassword?: string
  initialDeposit?: Money | null
  debtLimit?: Money | null
  dailyLimit?: Money | null
  monthlyLimit?: Money | null
  perTxnLimit?: Money | null
  contactName?: string
  contactPhone?: string
  contactNote?: string
  /**
   * Komissiya qoidasi — IXTIYORIY (egasi, 2026-08-20).
   *
   * ⚠️ Bo'sh qoldirilsa `PUT /commission-rules` UMUMAN chaqirilmaydi:
   * bo'sh qoida yozish «0% komissiya» ni ochib qo'yardi.
   */
  commission?: CommissionFormValues
}

/**
 * Agentlar va resellerlar sahifasi — BITTA komponent, IKKI marshrut.
 *
 * <h2>Nega bitta komponent</h2>
 * Agent va reseller — bitta daraxtdagi bitta jadval, farqi faqat
 * `kind` ustunida (01-DOMAIN-MODEL.md §5). Ikkita sahifa yozish daraxt,
 * balans va komissiya mantiqini ikki nusxaga bo'lardi.
 *
 * ⚠️ LEKIN RO'YXAT FILTRLANISHI SHART. Ilgari ikkala marshrut ham bir xil,
 * filtrlanmagan ro'yxatni ko'rsatardi: "Resellerlar" ga bosgan odam
 * agentlarni ham ko'rardi va menyudagi ikki band bir xil ekran ochardi.
 * Endi marshrut `mode` ni beradi va u uch narsani boshqaradi: ro'yxat
 * filtri, sarlavha, va yangi tugun yaratishda oldindan tanlangan tur.
 *
 * DARAXT `roots` + `children` orqali quriladi, butun ro'yxatni bir marta
 * olib emas. Ikki sabab:
 *   1. `GET /api/agents` `q` parametrisiz 401 qaytaradi (`agentApi.ts` izohi)
 *   2. Reseller iyerarxiyasi chuqur bo'lishi mumkin va uni to'liq yuklash
 *      katta o'rnatmada keraksiz
 *
 * ⚠️ OST-TUGUNLAR FILTRLANMAYDI. Reseller ostida agentlar turishi
 * NORMAL holat, va "shu resellerning tarmog'i" degan savolga javob
 * ularsiz to'liq bo'lmaydi. Turi har qatorda teg bilan ko'rinib turadi.
 */
export function AgentsPage() {
  const { t, i18n } = useTranslation()
  const { modal } = App.useApp()
  const queryClient = useQueryClient()
  const { user, can } = useAuth()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const currency = user?.tenant.currency ?? getCurrencyCode()

  const mode: 'AGENT' | 'RESELLER' = pathname.startsWith('/resellers') ? 'RESELLER' : 'AGENT'
  const isReseller = mode === 'RESELLER'

  const [selected, setSelected] = useState<AgentNode | null>(null)
  /*
    ⚠️ RO'YXAT HOLATI MANZILDA (JADVAL-QOIDASI §2). Qidiruv, tur va
    holat filtri, tartib — hammasi URL'da: sahifa yangilanganda filtr
    yo'qolmaydi va havolani ulashish mumkin.

    ⚠️ QIDIRUV KECHIKISH BILAN. Ilgari bu yerda oddiy `useState` turardi
    va `queryKeys.agents.list(search)` HAR HARFDA o'zgarardi — ya'ni
    «toshkent» so'zi 8 ta `GET /api/agents?q=…` so'rovi berardi.
    `useLocalListState` 350 ms kechikish beradi (etalon jadval bilan
    AYNI qiymat).

    ⚠️ SUKUT TARTIBI — `createdAt descend` (egasi: «oxirgi qo'shilgan
    doim birinchi»). Bu YOLG'ON emas: `GET /api/agents/roots` da
    sahifalash UMUMAN yo'q, javobda BUTUN ro'yxat keladi, ya'ni «joriy
    sahifa» degan tushunchaning o'zi yo'q (`shared/lib/localList.ts`
    sarlavhasi). Server saralashni qabul qilmaydi — `AgentController`
    da `sort` parametri yo'q, faqat `q`.
  */
  const list = useLocalListState({ defaultSort: DEFAULT_AGENT_SORT })
  const search = list.search
  const kindFilter = list.filter('kind')
  const stateFilter = list.filter('state')
  const [createOpen, setCreateOpen] = useState(false)
  /*
    ⚠️ YARATISH ZANJIRI ATOMAR EMAS (`agentAccountApi` izohi): tugun bir
    servisda, login boshqasida ochiladi. Yarim bajarilgan holat SHU YERDA
    ko'rsatiladi — oyna yopilgandan keyin ham, chunki tugun ALLAQACHON
    yaratilgan va uni "yaratilmadi" deb ko'rsatish operatorni ikkinchi
    marta urinishga (va «kod band» javobiga) olib borardi.
  */
  const [incomplete, setIncomplete] = useState<
    readonly { step: AccountStep; reason: string }[]
  >([])
  const [depositOpen, setDepositOpen] = useState(false)
  /*
    ⚠️ TAHRIRLASH VA KO'CHIRISH — IKKI AYRIM AMAL, ikkita ayrim oyna.

    Ota tugunni odatiy tahrir formasiga qo'shish «nomini to'g'irlayman»
    degan so'rovni butun shoxni ko'chiradigan amalga aylantirardi —
    backend `AgentDraft` javadoc'ida aynan shu sabab bilan `parentId`
    ni kirishdan chiqarib tashlagan.
  */
  const [editOpen, setEditOpen] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)
  const [limitOpen, setLimitOpen] = useState(false)
  const [fundLimitOpen, setFundLimitOpen] = useState(false)
  const [commissionOpen, setCommissionOpen] = useState(false)

  // Marshrut almashganda tanlov TOZALANADI: `/agents` da tanlangan tugun
  // `/resellers` ro'yxatida bo'lmasligi mumkin va o'ng ustunda ro'yxatda
  // ko'rinmaydigan tugunning balansi qolib ketardi.
  // ⚠️ Filtrlar ham tozalanadi: `/agents` da qo'yilgan «Turi: Agent»
  // `/resellers` ro'yxatini BO'SH qoldirardi va operator sababini
  // ko'rmasdi (ikkala marshrut bitta komponent, URL esa boshqa).
  const listReset = list.reset
  /*
    ⚠️ Marshrut HAQIQATAN o'zgargandagina tozalanadi. `listReset`
    havolasi har chizishda yangilanishi mumkin (u `useSearchParams` ga
    tayanadi) va shartsiz effekt URL'ni cheksiz qayta yozardi — qidiruv
    maydoni har harfda o'chib qolardi.
  */
  const previousMode = useRef(mode)
  useEffect(() => {
    if (previousMode.current === mode) return
    previousMode.current = mode
    setSelected(null)
    listReset()
  }, [mode, listReset])

  const roots = useQuery({ queryKey: queryKeys.agents.all, queryFn: fetchAgentRoots })

  const children = useQuery({
    queryKey: queryKeys.agents.children(selected?.id ?? ''),
    queryFn: () => fetchAgentChildren(selected?.id ?? ''),
    enabled: selected !== null,
  })

  const found = useQuery({
    queryKey: queryKeys.agents.list(search),
    queryFn: () => searchAgents(search),
    enabled: search.trim().length > 0,
  })

  const balance = useQuery({
    queryKey: queryKeys.agents.balance(selected?.id ?? ''),
    queryFn: () => fetchAgentBalance(selected?.id ?? ''),
    enabled: selected !== null,
  })

  /*
    ⚠️ PUL limiti FAQAT PUL OLADIGAN tugunda so'raladi. Toza resellerda
    (`kind === 'RESELLER'`) `agent.fund` huquqi umuman yo'q
    (`PartnerKind.RESELLER`), ya'ni u hech qachon to'ldirmaydi va uning
    kunlik limiti hech qachon sarflanmasdi — bo'sh jadval ko'rsatish
    "sozlash kerak" degan yolg'on taassurot berardi.
  */
  /*
    ⚠️ Mezon TUR emas, KASSA bayrog'i. Kassasi yoqilgan reseller
    `agent.fund` huquqini OLADI (`PartnerKind.permissions(cashDesk)`),
    ya'ni u pul to'ldiradi va limitlari ishlaydi.

    ⚠️ `kind` sharti QOLADI: `app_user_cash_desk_ok` CHECK'i AGENT'da
    `cash_desk` ni HAR DOIM `false` qiladi, ya'ni faqat bayroqqa
    o'tilsa limitlar HAMMA agentdan yashirinardi.

    ⚠️ `!== true`, `=== false` emas: `null` — «bilmaymiz», va
    bilmaganda hozirgi xulq (yashirish) saqlanadi.
  */
  const fundLimitsShown =
    selected !== null && (selected.kind !== 'RESELLER' || selected.cashDesk === true)

  const fundLimits = useQuery({
    queryKey: queryKeys.agents.fundLimits(selected?.id ?? ''),
    queryFn: () => fetchFundLimits(selected?.id ?? ''),
    retry: false,
    enabled: fundLimitsShown,
  })

  const rules = useQuery({
    queryKey: queryKeys.agents.commissionRules(selected?.id ?? ''),
    queryFn: () => fetchCommissionRules(selected?.id ?? ''),
    enabled: selected !== null,
  })

  const runner = useOperationRunner([
    queryKeys.agents.balance(selected?.id ?? ''),
    queryKeys.agents.fundLimits(selected?.id ?? ''),
    queryKeys.agents.all,
  ])

  /*
    ⚠️ KATALOG OYNA YOPIQ TURGANDA HAM KERAK (tester-2, BUG-018).

    Bu yerda `usePlanOptions(commissionOpen)` yozilgan edi, ya'ni paketlar
    ro'yxati FAQAT «Qoida o'rnatish» oynasi ochilganda so'ralardi. Komissiya
    JADVALI esa o'sha ro'yxatdan nom oladi va oyna yopiq bo'lgani uchun u
    HAR DOIM bo'sh bo'lardi — natijada `plans.find(...) ?? row.planId`
    zaxirasi ishga tushib, «Paket» ustunida xom UUID turardi
    (o'lchandi: `2f6b1c1e-0000-4000-8000-abcdefabcdef`).

    Endi ro'yxat agent TANLANGANDA so'raladi — aynan komissiya qoidalari
    bilan bir vaqtda. Qo'shimcha yuk yo'q: `usePlanOptions` React Query
    keshida 5 daqiqa yashaydi va boshqa ekranlar bilan bitta kalitni
    baham ko'radi.
  */
  const plans = usePlanOptions(selected !== null || commissionOpen)

  /** Katalogni umuman o'qiy oladimi — pastdagi «Paket» ustuni uchun. */
  const canReadPlans = can('plan.read')

  /** `user.create` — login/parol bo'limi; usiz faqat tugun ochiladi */
  const canCreateUser = can('user.create')
  /** `agent.deposit.add` — depozit va qarz limiti */
  const canFund = can('agent.deposit.add')

  const [createForm] = Form.useForm<CreateValues>()
  /*
    Boshlang'ich depozit uchun idempotentlik kaliti.

    ⚠️ Imzoda KOD va SUMMA bor: xato tufayli forma qayta yuborilganda
    kalit o'zgarmaydi (tarmoq uzilsa ikkinchi to'lov yozilmaydi), summa
    yoki kod to'g'irlansa esa bu BOSHQA amal va unga yangi kalit kerak.
  */
  const createCode = Form.useWatch('code', createForm)
  const createDeposit = Form.useWatch('initialDeposit', createForm)
  const createKey = useIdempotencyKey(
    'agent-create',
    [createCode ?? '', createDeposit?.amount ?? ''].join(':'),
  )
  /*
    ⚠️ Yaratish formasidagi komissiya BLOKI HAM tirik tekshiriladi —
    muharrir oynasidagi bilan AYNI hook. Aks holda zinapoya yaroqsiz
    bo'lsa forma uni serverga yuborardi va agent yaratilgandan KEYIN
    400 chiqardi: eng yomon payt, chunki tugun allaqachon bor.

    ⚠️ Bitta to'lov limiti FORMADAN olinadi: yangi agentda hali
    saqlangan limit yo'q, ya'ni «o'lik pog'ona» ogohlantirishi aynan
    shu ekranda kiritilayotgan qiymatga qarab chiqishi kerak.
  */
  const createPerTxn = Form.useWatch('perTxnLimit', createForm)
  const createCommission = useCommissionState(
    createForm,
    createPerTxn?.amount ?? null,
    COMMISSION_PREFIX,
  )

  /*
    ⚠️ Bo'lim FAQAT huquq bilan chiziladi. Tur bo'yicha to'siq YO'Q:
    2026-08-21 dan beri reseller ham komissiya oladi
    (`COMMISSION_EARNING_KINDS`).

    `agent.commission.set` yo'q operator uchun bo'lim chizilsa, u
    to'ldirilib, keyin agent yaratilgandan KEYIN 403 bilan
    qaytarilardi — huquqi yo'q amal KO'RINMASIN (JADVAL-QOIDASI §3).
  */
  const commissionOnCreate =
    can('agent.commission.set') && COMMISSION_EARNING_KINDS.includes(mode)

  const [depositForm] = Form.useForm<{ amount: Money | null }>()
  // ⚠️ Imzoda SUMMA bor: `FAILED` da oyna ochiq qoladi, operator summani
  // to'g'irlab qayta yuborishi mumkin — bu boshqa amal, boshqa kalit.
  const depositAmount = Form.useWatch('amount', depositForm)
  const depositKey = useIdempotencyKey(
    'agent-deposit',
    [selected?.id ?? '', depositAmount?.amount ?? ''].join(':'),
  )
  const [limitForm] = Form.useForm<{ amount: Money | null }>()
  const [fundLimitForm] = Form.useForm<{
    daily: Money | null
    monthly: Money | null
    perTxn: Money | null
  }>()

  /*
    ⚠️ IKKALA MUTATSIYANING XATOSI OYNANING ICHIDA ko'rsatiladi
    (`VoucherVoidModal` naqshi), sahifa banneriga EMAS.

    Ilgari xabar sahifaga yozilardi, oyna esa OCHIQ qolardi — matn modal
    niqobi ortida turardi va operator faqat spinner to'xtaganini
    ko'rardi. "Kod band" (409) kabi xabar esa aynan formani TUZATISH
    uchun kerak, ya'ni u forma bilan bir ekranda bo'lishi shart.

    Depozit va qarz limiti oynalari bunga muhtoj emas: ular
    `useOperationRunner` orqali ishlaydi va natijani (`FAILED` ham)
    o'zi ko'rsatadi.
  */
  const create = useMutation({
    mutationFn: (values: CreateValues) =>
      createAgentAccountWithCommission(
        {
          code: values.code,
          displayName: values.displayName,
          // ⚠️ Tur MARSHRUTDAN, ikkala formada ham tanlagich yo'q:
          // "Agentlar" da faqat agent, "Resellerlar" da faqat reseller —
          // boshqa yo'l umuman ochilmaydi.
          kind: mode,
          settlementMode: values.settlementMode,
          parentId: values.parentId ?? null,
          username: values.username?.trim() ? values.username.trim() : null,
          rawPassword: values.rawPassword ?? null,
          // Serverda `cash_desk` faqat RESELLER da ruxsat etilgan
          // (`app_user_cash_desk_ok`) — agent formasida bayroq umuman
          // chizilmaydi va bu yerda MAJBURAN o'chiriladi.
          cashDesk: isReseller && values.cashDesk === true,
          initialDeposit: values.initialDeposit ?? null,
          debtLimit: values.debtLimit ?? null,
          dailyLimit: values.dailyLimit ?? null,
          monthlyLimit: values.monthlyLimit ?? null,
          perTxnLimit: values.perTxnLimit ?? null,
          // Kontakt `platform.contact` ga yoziladi (ADR-031). Bo'sh satr
          // `null` ga aylanadi — serverdagi CHECK bilan bir xil qoida.
          contactName: values.contactName?.trim() || null,
          contactPhone: values.contactPhone?.trim() || null,
          contactNote: values.contactNote?.trim() || null,
        },
        /*
          ⚠️ `agentId` BU YERDA YO'Q va bo'lishi ham mumkin emas: u
          birinchi so'rovdan keyin tug'iladi. Shuning uchun qoida
          «agentsiz draft» sifatida uzatiladi va id ni
          `createAgentAccountWithCommission` o'zi qo'yadi.
        */
        isCommissionRequested(values.commission)
          ? toCommissionRuleWithoutAgent(values.commission ?? {})
          : null,
        createKey,
        t,
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.tree })
      if (result.node?.parentId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.agents.children(result.node.parentId),
        })
      }
      setIncomplete(result.failed)
      createForm.resetFields()
      setCreateOpen(false)
    },
  })

  /*
    Komissiya qoidasini bekor qilish (BUG-020).

    ⚠️ Xato JIMGINA yutilmaydi. Bu amal pulga ta'sir qiladi va agar
    server rad etsa (masalan qoida boshqa agentniki yoki allaqachon
    yopilgan) — operator buni KO'RISHI kerak, aks holda u qoida
    yopilgan deb o'ylab ketardi va farq oy oxirida chiqardi.
  */
  const closeCommissionRuleMutation = useMutation({
    mutationFn: (ruleId: string) => closeCommissionRule(selected?.id ?? '', ruleId),
    onSuccess: () => {
      if (selected) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.agents.commissionRules(selected.id),
        })
      }
    },
  })

  const closeCreate = () => {
    create.reset()
    setCreateOpen(false)
  }

  // Filtr MIJOZDA: backend `kind` bo'yicha so'rovni qabul qilmaydi
  // (`AgentController.list` faqat `q` oladi). Tugunlar soni yuzlab
  // bo'lgani uchun bu yetarli; minglab bo'lganda serverga ko'chirish kerak.
  /**
   * ⚠️ YOPILGAN TUGUNLAR STANDART HOLDA YASHIRILADI.
   *
   * Abonentlar ro'yxatidagi bilan AYNI muammo va ayni sabab: yopilgan
   * agent o'chirilmaydi (uning sotuvlari va komissiyasi tarixda qoladi),
   * ya'ni ularning soni faqat o'sadi. Staging'da 62 tadan atigi 2 tasi
   * faol edi — ro'yxat ochilganda ekran to'la "Yopilgan" qatorlar
   * ko'rinardi va faol agentni topib bo'lmasdi.
   *
   * ⚠️ QIDIRUV NATIJASIGA QO'LLANMAYDI. Odam biror agentni ATAYLAB
   * qidirganda uni topa olmasligi — eng yomon holat: u yopilgan bo'lsa
   * ham natijada ko'rinishi kerak, aks holda "bunday agent yo'q" degan
   * xato xulosa chiqarilardi.
   *
   * ⚠️ «Yopilganlarni ko'rsatish» BELGILAGICHI OLIB TASHLANDI (egasi
   * qarori 2026-08-20, abonentlarda ham shu). Ikkinchi boshqaruv
   * holat filtridagi `CLOSED` bilan ikkilanish paydo qilardi va
   * ikkovining munosabati («biri ikkinchisini o'chiradi») hech qayerda
   * tushuntirilmasdi. Yagona yo'l — HOLAT filtri.
   */
  /*
    ⚠️ TURI NOMA'LUM tugun (`kind === null`) IKKALA ro'yxatda ham KO'RINADI.
    Sabab `agentApi` sxemasi izohida: `null` — "bu tur shu servis
    modellaydigan uchtadan biri emas", ya'ni administrator ochgan yangi tur.
    Uni ikkala ro'yxatdan ham chiqarib tashlash tugunni PANELDAN BUTUNLAY
    yo'qotardi — u bazada bor va hisob-kitob qilinadi, lekin hech qaysi
    ekranda ko'rinmasdi.
  */
  const visible = (node: AgentNode) =>
    node.kind === null || VISIBLE_KINDS[mode].includes(node.kind)

  /** Marshrutga mos, lekin hali FILTRLANMAGAN qatorlar — «nechtadan nechtasi» uchun */
  const sourceRows: readonly AgentNode[] = (
    search.length > 0 ? (found.data?.nodes ?? []) : (roots.data ?? [])
  )
    .filter(visible)
    /*
      ⚠️ HOLAT FILTRI STANDART YASHIRISHNI BEKOR QILADI.

      Yopilganlar standart holda ro'yxatda yo'q, lekin operator
      «Holat: Yopilgan» ni ATAYLAB tanlasa ro'yxat BO'SH chiqardi —
      ikkita qoida bir-biriga qarshi ishlab, natijasi sababsiz
      bo'shlik bo'lardi. Tanlov bor joyda standart taxmin chekinadi.
    */
    .filter((node) => stateFilter !== null || search.length > 0 || node.state !== 'CLOSED')

  /*
    ⚠️ TUR VA HOLAT FILTRI + `createdAt desc` TARTIBI.

    Ikkalasi ham MIJOZDA va bu shu endpointda yolg'on emas: javobda
    butun ro'yxat keladi, sahifalash yo'q. Server tomonda ular MAVJUD
    EMAS — `AgentController.list` faqat `q` oladi, `roots` esa umuman
    parametrsiz. Hisobotda shu aytilgan.
  */
  const treeRows = useMemo(
    () =>
      applyLocalList(sourceRows, {
        search: '',
        filters: {
          kind: (node, value) => node.kind === value,
          state: (node, value) => node.state === value,
        },
        filterState: list.state.filters,
        comparators: {
          createdAt: byInstant((node) => node.createdAt),
          code: byText((node) => node.code),
          displayName: byText((node) => node.displayName),
          state: byText((node) => node.state),
        },
        sort: list.sort,
      }),
    [sourceRows, list.state.filters, list.sort],
  )

  /*
    ⚠️ QIDIRUV NATIJASI BIR SAHIFA BILAN CHEKLANGAN VA BU AYTILADI.

    `GET /api/agents` 2026-08-20 dan sahifalangan; panel bir so'rovda
    eng ko'pi `MAX_PAGE_SIZE` qator oladi va serverning `total` ini
    biladi. Bu ekran DARAXT — unda sahifa tugmalari yo'q, shuning uchun
    kesilgani banner bilan aytiladi. Jimgina qoldirish «bizda shuncha
    agent bor» degan xato xulosaga olib borardi.
  */
  const searchTotal = found.data?.total ?? 0
  const searchShown = found.data?.nodes.length ?? 0
  const searchTruncated = search.length > 0 && searchTotal > searchShown

  /*
    ⚠️ OST-TUGUNLAR SARALANADI, LEKIN FILTRLANMAYDI.

    Reseller ostida agentlar turishi NORMAL holat va «shu resellerning
    tarmog'i» degan savolga javob ularsiz to'liq bo'lmaydi — ya'ni
    yuqoridagi tur filtri bu jadvalga qo'llanmasligi KERAK. Tartib esa
    ikkala jadvalda bir xil bo'lishi shart, aks holda ro'yxat ikki xil
    qonun bo'yicha o'qilardi.
  */
  const childRows = useMemo(
    () =>
      applyLocalList(children.data ?? [], {
        search: '',
        filterState: {},
        comparators: {
          createdAt: byInstant((node) => node.createdAt),
          code: byText((node) => node.code),
          displayName: byText((node) => node.displayName),
          state: byText((node) => node.state),
        },
        sort: list.sort,
      }),
    [children.data, list.sort],
  )

  /*
    ⚠️ RO'YXATDA HAM OGOHLANTIRISH BO'LISHI SHART (egasi, 2026-08-19).

    Staging'da 6 ta agentda umumiy komissiya qoidasi yo'q — ular
    cashback OLMAYDI va buni hech kim ko'rmayapti, chunki belgi faqat
    tanlangan agentning kartochkasida bo'lardi. Ro'yxat esa aynan
    «qaysi biri sozlanmagan» degan savolga javob beradigan joy.

    ⚠️ SO'ROVLAR SONI CHEKLANGAN: faqat KO'RINADIGAN, YOPILMAGAN va
    komissiya OLADIGAN tugunlar so'raladi. Yopilganlar standart holda
    ro'yxatda yo'q, turi NOMA'LUM tugun esa bu servis modellaydigan
    ikkitadan biri emas (`AgentKind.fromCode` → `null`) — ular uchun
    so'rov yuborish o'nlab keraksiz chaqiruv bo'lardi.
  */
  const commissionCheckIds = [...treeRows, ...(children.data ?? [])]
    .filter(
      (node) =>
        node.state !== 'CLOSED' && node.kind !== null && COMMISSION_EARNING_KINDS.includes(node.kind),
    )
    .map((node) => node.id)
    .filter((id, index, all) => all.indexOf(id) === index)

  const commissionChecks = useQueries({
    queries: commissionCheckIds.map((id) => ({
      // Kalit kartochkanikiga TENG — agent tanlanganda so'rov qaytadan
      // yuborilmaydi, keshdagi javob ishlatiladi.
      queryKey: queryKeys.agents.commissionRules(id),
      queryFn: () => fetchCommissionRules(id),
      retry: false,
    })),
  })

  /*
    ⚠️ FAQAT MUVAFFAQIYATLI javob belgi beradi. Yuklanmagan yoki 403
    bilan qaytgan so'rovni «qoida yo'q» deb ko'rsatish PANEL BILMAGAN
    narsani bilaman deb ko'rsatish bo'lardi — va bu ogohlantirishning
    o'zini ishonchsiz qilardi.
  */
  const missingRuleIds = new Set(
    commissionCheckIds.filter((_id, index) => {
      const query = commissionChecks[index]
      return query?.isSuccess === true && !query.data.some(isGeneralOpenRule)
    }),
  )

  const treeColumns: ColumnsType<AgentNode> = [
    {
      /*
        ⚠️ Ikkala jadval ham (ildizlar va ost-tugunlar) SAHIFALANMAYDI —
        `pagination={false}`, ya'ni ro'yxat butunligicha ekranda. Shuning
        uchun raqam to'g'ridan-to'g'ri `index` dan olinadi va har jadval
        o'z ichida 1 dan boshlanadi.
      */
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'code',
      title: t('agents.code'),
      /*
        ⚠️ ENI BERILISHI SHART. Qolgan uch ustunda qat'iy en bor
        (120+130+120 = 370px), bu ustunda esa yo'q edi — ya'ni u
        "qolganini oladi". Jadval `lg={14}` ustunida turgani uchun
        1000px oynada kartaga ~400px tegadi va bu ustunga 30px qoladi:
        agent nomi HARFLAB TIK yozilardi.

        En + `scroll.x` birgalikda ishlaydi: ustunlar siqilmaydi, jadval
        esa karta ichida yon tomonga suriladi.
      */
      width: 220,
      ...sortColumn(list.sort, 'code'),
      render: (_, row) => (
        <Space>
          <Typography.Text strong>{row.displayName}</Typography.Text>
          <Typography.Text type="secondary">{row.code}</Typography.Text>
        </Space>
      ),
    },
    {
      key: 'kind',
      title: t('agents.kind'),
      width: 120,
      // Agent va Reseller farqi ustunda ko'rinib turishi kerak: birinchisi
      // komissiya oladi, ikkinchisi o'z narxi bilan sotadi.
      // ⚠️ `null` — "bu tur shu servis modellaydigan uchtadan biri emas"
      // (`agentApi` sxemasi). Chiziqcha qo'yamiz, taxmin qilmaymiz.
      render: (_, row) =>
        row.kind === null ? (
          <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
        ) : (
          <Tag color={KIND_COLOR[row.kind]}>{t(`agentKind.${row.kind}`)}</Tag>
        ),
    },
    {
      key: 'settlement',
      title: t('agents.settlementMode'),
      width: 130,
      render: (_, row) =>
        row.settlementMode === null ? t('common.dash') : t(`settlementMode.${row.settlementMode}`),
    },
    {
      key: 'state',
      title: t('subscribers.columns.status'),
      width: 120,
      render: (_, row) => (
        <Tag color={row.state === 'ACTIVE' ? 'green' : 'default'}>
          {t(`agentState.${row.state}`)}
        </Tag>
      ),
    },
    {
      // Yuqoridagi `missingRuleIds` izohiga qarang.
      key: 'commission',
      // ⚠️ Kartaning sarlavhasidan BOSHQA kalit: bu ustun qoidaning
      // BORLIGI haqida, karta esa qoidalar ro'yxati haqida.
      title: t('agents.commissionRuleColumn'),
      width: 150,
      render: (_, row) =>
        missingRuleIds.has(row.id) ? (
          <Tooltip title={t('agents.commissionMissingHint')}>
            <Tag color="warning">{t('agents.commissionMissingShort')}</Tag>
          </Tooltip>
        ) : null,
    },
    {
      /*
        ⚠️ «Qachon qo'shilgan» USTUNI — sukut tartibining ASOSI.

        Tartib `createdAt desc`, ya'ni ekrandagi qatorlar tartibini
        tushuntiradigan ustun ko'rinib turishi kerak. Usiz operator
        «nega bu birinchi turibdi?» degan savolga javob topa olmasdi
        va tartibni tasodifiy deb qabul qilardi.

        ⚠️ `null` — server vaqtni BERMADI, «yaqinda» EMAS. Chiziqcha
        qo'yiladi va bunday qator tartibda OXIRDA qoladi.
      */
      key: 'createdAt',
      title: t('common.createdAt'),
      width: 170,
      ...sortColumn(list.sort, 'createdAt'),
      render: (_, row) =>
        row.createdAt === null ? (
          <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
        ) : (
          <Tooltip title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Tooltip>
        ),
    },
    {
      /*
        ⚠️ UCH NUQTA AMALLARI (JADVAL-QOIDASI §2).

        Ilgari qatorda hech qanday amal yo'q edi: operator qatorni
        bosib, o'ng ustunga qarab, o'sha yerdagi tugmani izlashi kerak
        edi — va o'ng ustun 1000px dan tor ekranda PASTGA tushib
        ketardi, ya'ni bosilgan qatordan keyin hech narsa
        o'zgarmagandek ko'rinardi.

        ⚠️ Har amal AVVAL qatorni TANLAYDI. Modal `selected` ga
        tayanadi va uni qo'ymasdan ochish oldingi agentga pul
        yozdirib yuborardi.

        ⚠️ Huquqi yo'q amal KO'RINMAYDI — `RowActions` ning
        `permission` mexanizmi (JADVAL-QOIDASI §3).
      */
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              /*
                ⚠️ HUQUQ `agent.create` — katalogda `agent.update` YO'Q va
                backend shuning uchun tahrirlash, ko'chirish va holatni
                aynan shu huquq bilan himoyalaydi (`AgentController`
                javadoc'i). `agent.read` bilan ko'rsatish tugmani
                bosilib, keyin 403 beradigan qilardi.
              */
              key: 'edit',
              label: t('agents.edit.action'),
              onClick: () => {
                setSelected(row)
                setEditOpen(true)
              },
              permission: 'agent.create',
              /*
                ⚠️ Hisob-kitob usuli NOMA'LUM tugun tahrirlanmaydi.
                Server `settlement_mode` ni SHARTSIZ yozadi va bo'sh
                qiymatni `DEPOSIT` ga aylantiradi
                (`AgentDraft.settlementModeOrDefault`) — ya'ni «nomini
                to'g'irladim» degan amal tugunga jimgina hisob-kitob
                usuli yozib qo'yardi. Bunday tugun «Tuzilma» sahifasida
                tahrirlanadi.
              */
              hidden: row.settlementMode === null,
            },
            {
              key: 'move',
              label: t('agents.move.action'),
              onClick: () => {
                setSelected(row)
                setMoveOpen(true)
              },
              permission: 'agent.create',
            },
            /*
              ⚠️ IKKI O'TISH HAVOLASI — AMALLAR ORASIDA, lekin ALOHIDA
              guruhda (`divider`).

              «Bu agent qancha pul oborot qildi?» va «nechta paket
              sotdi?» — bu sahifadagi eng ko'p beriladigan ikki savol,
              va ularning javobi boshqa sahifalarda TAYYOR turardi.
              Yetishmagani — YO'L: operator tranzaksiyalar sahifasiga
              o'zi borib, filtr panelini ochib, u yerdan agentning
              UUID'ini SO'RAGAN maydonga duch kelardi. UUID esa panelda
              hech qayerda ko'rinmaydi, ya'ni javob amalda yopiq edi.

              ⚠️ Filtr URL da (`agentLinks`), marshrut holatida EMAS:
              sahifa yangilanganda yo'qolmasin va havolani yuborish
              mumkin bo'lsin.
            */
            {
              key: 'transactions',
              label: t('agents.links.transactions'),
              onClick: () => void navigate(agentTransactionsHref(row.id)),
              permission: 'ledger.read',
              divider: true,
            },
            {
              key: 'grants',
              label: t('agents.links.grants'),
              onClick: () => void navigate(agentGrantsHref(row.id)),
              /*
                ⚠️ `grant.read` YO'Q — katalogda bunday huquq umuman
                yozilmagan. `/grants` sahifasi menyuda `subscriber.read`
                ostida turadi (`navItems`), ya'ni AYNI shart bu yerda
                ham to'g'ri. O'ylab topilgan huquq nomi `Permission`
                tipida yiqilardi, mavjud boshqasini tanlash esa havolani
                huquqi bor odamdan yashirardi.
              */
              permission: 'subscriber.read',
            },
            {
              key: 'commission',
              label: t('agents.commissionSet'),
              onClick: () => {
                setSelected(row)
                setCommissionOpen(true)
              },
              permission: 'agent.commission.set',
              divider: true,
              // Turi noma'lum tugun bu servis modelidan tashqarida
              // (`COMMISSION_EARNING_KINDS`) — unga qoida yozib bo'lmaydi
              hidden: row.kind !== null && !COMMISSION_EARNING_KINDS.includes(row.kind),
            },
            {
              key: 'deposit',
              label: t('agents.deposit'),
              onClick: () => {
                setSelected(row)
                setDepositOpen(true)
              },
              permission: 'agent.deposit.add',
              divider: true,
            },
            {
              key: 'debtLimit',
              label: t('agents.debtLimit'),
              onClick: () => {
                setSelected(row)
                setLimitOpen(true)
              },
              permission: 'agent.deposit.add',
            },
            {
              key: 'fundLimits',
              label: t('agents.account.fundLimitTitle'),
              onClick: () => {
                setSelected(row)
                setFundLimitOpen(true)
              },
              permission: 'agent.deposit.add',
              // Toza resellerda `agent.fund` huquqi umuman yo'q — uning
              // kunlik limiti hech qachon sarflanmasdi (`fundLimitsShown`)
              // ⚠️ Kassasi bor reseller pul to'ldiradi — limitlari YASHIRILMAYDI.
              hidden: row.kind === 'RESELLER' && row.cashDesk !== true,
            },
          ]}
        />
      ),
    },
  ]

  /*
    ⚠️ FAQAT AMALDAGI QOIDALAR (X-SIB-009).

    Backend `GET /{id}/commission-rules` BUTUN TARIXNI qaytaradi: yangi
    qoida qo'yilganda eskisi o'chirilmaydi, `valid_to` bilan YOPILADI —
    "o'sha paytda shart qanday edi" savoliga javob qolishi kerak
    (`CommissionRuleDraft` javadoc'i).

    Jadval esa `valid_to` ni umuman ko'rsatmasdi. Natijada bitta paket
    uchun komissiyani ikki marta o'zgartirgan operator ikkita bir xil
    qatorni ko'rardi va "bir paketga bir nechta qoida yaratib bo'ldi"
    degan XATO xulosaga kelardi. Aslida ochiq qoida bitta va buni baza
    MAJBURLAYDI: `commission_rule_open_uq` — qisman unikal indeks
    (`V7_001__agent_init.sql`, `WHERE valid_to IS NULL`), ya'ni ikkinchi
    ochiq qoida ikki so'rov bir vaqtda kelganda ham yozilmaydi.

    Tarix yo'qolmaydi — jadval ostida nechta yopilgan qoida borligi
    yoziladi, ya'ni "qayerga ketdi" savoli ham javobsiz qolmaydi.
  */
  const openRules = (rules.data ?? []).filter((rule) => rule.validTo === null)
  const closedRuleCount = (rules.data ?? []).length - openRules.length

  /*
    ⚠️ «QOIDASI YO'Q» — JIMGINA NOL EMAS, OGOHLANTIRISH.

    O'lchandi (Staging, 2026-08-19): 6 ta agentda ochiq UMUMIY qoida
    yo'q. Ular cashback OLMAYDI, panel esa buni hech qanday shaklda
    aytmasdi — jadval shunchaki bo'sh turardi va bo'sh jadval
    "hozircha sozlanmagan" degan beg'ubor ma'no beradi.

    ⚠️ Paketga bog'langan qoida BU SAVOLGA JAVOB EMAS. Komissiya
    faqat balans to'ldirishda hisoblanadi, to'ldirishda esa paket
    yo'q (SHARTNOMA §1) — ya'ni faqat `planId === null` qoida
    haqiqatan ishlaydi.
  */
  const missingGeneralRule = rules.isSuccess && !(rules.data ?? []).some(isGeneralOpenRule)

  const commissionColumns: ColumnsType<CommissionRule> = [
    {
      // Ochiq qoidalar ro'yxati sahifalanmaydi
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'plan',
      /*
        ⚠️ XOM UUID CHIZILMAYDI — UCH HOLAT ALOHIDA (`AgentRef` qoidasi).

        Ilgari zaxira qiymat `?? row.planId` edi va u ekranga
        `2f6b1c1e-0000-4000-8000-abcdefabcdef` yozardi: operator qaysi
        paketga qoida qo'yilganini BILA OLMASDI (tester-2, BUG-018).

        · `planId === null` → «Barcha paketlar» (bu normal holat, xato emas)
        · katalog hali yuklanmagan → «Yuklanmoqda…», UUID EMAS: bo'sh
          ro'yxatni «paket topilmadi» deb ko'rsatish yolg'on bo'lardi
        · katalogda yo'q → sariq teg, to'liq id esa TOOLTIPDA qoladi
          (arxivlangan paket yoki osilgan havola — ikkalasi ham bo'lishi
          mumkin va farqni panel bilmaydi)

        ⚠️ Nom EKRAN uchun tarjima qilinadi (`displayName`), pastdagi
        tanlagich bilan bir xil manba — aks holda bitta paket jadvalda va
        oynada BOSHQA-BOSHQA nom bilan ko'rinardi. Mashina o'qiydigan
        yo'llar (eksport, QR) kanonik nomni oladi — `reference.ts` izohi.
      */
      title: t('agents.commissionPlan'),
      render: (_, row) => {
        if (row.planId === null) return t('agents.commissionAllPlans')
        const planId = row.planId
        const plan = plans.find((p) => p.id === planId)
        if (plan) return displayName(plan, i18n.language)
        if (plans.length === 0) {
          /*
            ⚠️ «YUKLANMOQDA…» FAQAT HUQUQ BOR BO'LGANDA.

            O'lchandi (Staging, `identity.role_permission`, 2026-08-16):
            `AGENT` rolida `agent.read` BOR, `plan.read` esa YO'Q
            (12 huquqdan bittasi ham katalogga tegmaydi), va shu roldagi
            5 ta haqiqiy foydalanuvchi bor. Menyudagi «Agentlar» bandi
            esa faqat `agent.read` bilan ochiladi
            (`navItems.tsx`), ya'ni ular bu jadvalni KO'RADI.

            Ularda `/api/catalog/plans` 403 qaytaradi → ro'yxat abadiy
            bo'sh → «Yuklanmoqda…» hech qachon tugamaydi. Bu xom UUID
            dan ham yomon: UUID hech bo'lmasa rost edi, «yuklanmoqda»
            esa YOLG'ON va operator kutib turaveradi.

            Huquq yo'q bo'lsa chiziqcha va SABABI — panel bilmagan
            narsani bilaman deb ko'rsatmaydi.
          */
          return canReadPlans ? (
            <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
          ) : (
            <Tooltip title={t('errors.forbidden')}>
              <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
            </Tooltip>
          )
        }
        return (
          <Tooltip title={t('agents.commissionPlanUnknownHint')}>
            <Tag color="warning">{t('agents.commissionPlanUnknown')}</Tag>
          </Tooltip>
        )
      },
    },
    {
      key: 'value',
      title: t('agents.commissionValue'),
      width: 220,
      align: 'right',
      render: (_, row) =>
        /*
          ⚠️ ZINAPOYA BITTA SONGA SIG'MAYDI. Uni «—» yoki birinchi
          pog'onaning foizi bilan ko'rsatish YOLG'ON bo'lardi: operator
          bitta raqamni ko'rib, qolgan pog'onalar yo'q deb o'ylardi.
          Shuning uchun har pog'ona alohida qatorda, chegara ma'nosi
          bilan («10 000 gacha»).
        */
        row.kind === 'TIER' ? (
          <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
            {row.tiers.length === 0 ? (
              <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
            ) : (
              row.tiers.map((tier) => (
                <Typography.Text key={tier.seq} style={{ fontSize: 12 }}>
                  {tier.upToAmount === null
                    ? t('agents.commissionTier.andAbove')
                    : t('agents.commissionTier.upToValue', {
                        amount: formatMoneyValue(tier.upToAmount),
                      })}
                  {' · '}
                  {tier.percentBp === null
                    ? formatMoneyValue(tier.fixedAmount)
                    : `${(tier.percentBp / 100).toFixed(2)}%`}
                </Typography.Text>
              ))
            )}
          </Space>
        ) : row.kind === 'PERCENT' ? (
          /*
              Bazis punktdan foizga qaytarish.

              ⚠️ SONDAN KEYIN BO'SH JOY YO'Q (tester-2, BUG-002). Bu yerda
              `${...} %` yozilgan edi, panelning qolgan ikki foiz
              ko'rsatkichi esa antd `Statistic suffix="%"` bilan chiziladi
              (bosh sahifadagi «Rad etildi» va kampaniya statistikasi) —
              ular `25%` beradi. Ya'ni bitta seansda foiz IKKI XIL
              ko'rinardi: `25%` va `3.00 %`. Bo'shliqsiz shakl tanlandi,
              chunki u ikkita ekranda, bo'shliqli esa bittasida edi.
            */
          <>{`${((row.percentBp ?? 0) / 100).toFixed(2)}%`}</>
        ) : (
          <>{formatMoneyValue(row.fixedAmount)}</>
        ),
    },
    {
      /*
        ⚠️ «QACHONDAN» USTUNI — SHARTNOMA §5. Qoida kelajakdagi ondan
        kuchga kirishi mumkin, ya'ni jadvalda ochiq turgan qoida HOZIR
        ishlayotgan bo'lmasligi ham mumkin. Sanani ko'rsatmaslik
        operatorga «foiz o'zgardi» degan yolg'on aytardi.
      */
      key: 'validFrom',
      title: t('agents.commissionValidFrom'),
      width: 150,
      render: (_, row) =>
        row.validFrom === null ? (
          <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
        ) : (
          <Typography.Text type={row.validFrom > new Date().toISOString() ? 'warning' : undefined}>
            {formatDateTime(row.validFrom)}
          </Typography.Text>
        ),
    },
    /*
      ⚠️ BEKOR QILISH (BUG-020) — «o'chirish» EMAS.

      Tester: «Qo'shilgan komissiyani o'chirib bo'mayabdi» — jadvalda
      «Amallar» ustuni ham, tugma ham yo'q edi.

      Server qatorni O'CHIRMAYDI, `valid_to` qo'yib YOPADI: shu qoida
      bo'yicha hisoblangan komissiyalar jurnalda turibdi va ular qaysi
      shart asosida chiqqanini tushuntirib bera olish kerak. Shuning
      uchun matn ham «o'chirish» emas, «bekor qilish» — tugma nima
      qilishini AYTADI.

      Yopilgan qoida bu jadvaldan yo'qoladi (yuqoridagi `openRules`
      filtri) va ostidagi "yopilganlar soni" ga qo'shiladi — ya'ni
      "qayerga ketdi" savoli javobsiz qolmaydi.

      ⚠️ Tasdiq MAJBURIY: bu amal PULGA ta'sir qiladi va natijasi oy
      oxirida, komissiya hisoblanganda ko'rinadi — tasodifiy bosilgan
      tugmani darhol sezib bo'lmaydi.
    */
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'closeRule',
              label: t('agents.commissionClose'),
              onClick: () => {
                modal.confirm({
                  title: t('agents.commissionCloseConfirm'),
                  content: t('agents.commissionCloseHint'),
                  okText: t('common.yes'),
                  cancelText: t('common.no'),
                  okButtonProps: { danger: true },
                  onOk: () => closeCommissionRuleMutation.mutate(row.id),
                })
              },
              permission: 'agent.commission.set',
              loading: closeCommissionRuleMutation.isPending,
              danger: true,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={isReseller ? t('nav.resellers') : t('nav.agents')}
        description={isReseller ? t('agents.introReseller') : t('agents.introAgent')}
        extra={
          <Can permission="agent.create">
            {/* ⚠️ Yorliq TANLOVGA bog'liq EMAS. Ilgari u «Yangi agent» dan
                «Yangi quyi agent» ga jimgina o'zgarardi va operator nima
                yaratayotganini tugmadan bilmasdi. Endi ota tugun formada
                OSHKORA maydon, ya'ni tugma bitta narsani aytadi. */}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t(isReseller ? 'agents.createRootReseller' : 'agents.createRoot')}
            </Button>
          </Can>
        }
      />

      {incomplete.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setIncomplete([])}
          message={t('agents.account.partial')}
          description={
            <>
              <ul style={{ margin: '0 0 8px', paddingInlineStart: 20 }}>
                {incomplete.map((item) => (
                  <li key={item.step}>
                    {t(`agents.account.step.${item.step}`)} — {item.reason}
                  </li>
                ))}
              </ul>
              {/*
                ⚠️ KOMISSIYA ALOHIDA JUMLA BILAN, umumiy matnga
                QO'SHILMAYDI (egasi, 2026-08-20). Bu qadam yiqilganda
                agent ALLAQACHON yaratilgan va u komissiyasiz ishlab
                turadi — cashback esa jimgina hisoblanmaydi. Operator
                aynan «qayta urinib ko'ring» degan ko'rsatmani olishi
                kerak, «amal to'liq bajarilmadi» degan umumiy matnni
                emas.
              */}
              {incomplete.some((item) => item.step === 'commission') ? (
                <Typography.Paragraph strong style={{ marginBottom: 8 }}>
                  {t('agents.account.commissionFailedHint')}
                </Typography.Paragraph>
              ) : null}
              <Typography.Text type="secondary">{t('agents.account.partialHint')}</Typography.Text>
            </>
          }
        />
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card
            title={
              search
                ? t('agents.searchResults')
                : t(isReseller ? 'agents.rootsReseller' : 'agents.rootsAgent')
            }
          >
            {/* ⚠️ Qidiruv va filtrlar KARTA SARLAVHASIDAN ICHKARIGA
                ko'chirildi: sarlavhadagi `extra` tor ekranda ikkinchi
                qatorga tushib, uchta boshqaruv bir-birining ustiga
                chiqardi. Ustun voronkasi ham ISHLATILMAYDI — etalon
                jadvalda o'lchangan: ko'rinmagan boshqaruv mavjud emas
                bilan barobar. */}
            <Flex wrap gap="small" align="center" style={{ marginBottom: 16 }}>
              <Input.Search
                allowClear
                value={list.searchText}
                onChange={(e) => list.setSearchText(e.target.value)}
                placeholder={t(
                  isReseller ? 'agents.searchPlaceholderReseller' : 'agents.searchPlaceholder',
                )}
                style={{ width: 240, maxWidth: '100%' }}
              />
              {/* Turlar ro'yxati AYNAN marshrut filtridan (`VISIBLE_KINDS`):
                  ro'yxatda ko'rinmaydigan turni tanlash mumkin bo'lsa,
                  natija doim bo'sh chiqardi. */}
              <Select<string>
                allowClear
                value={kindFilter}
                onChange={(value) => list.setFilter('kind', value ?? null)}
                placeholder={t('agents.kind')}
                style={{ width: 150 }}
                options={VISIBLE_KINDS[mode].map((k) => ({
                  value: k,
                  label: t(`agentKind.${k}`),
                }))}
              />
              <Select<string>
                allowClear
                value={stateFilter}
                onChange={(value) => list.setFilter('state', value ?? null)}
                placeholder={t('subscribers.columns.status')}
                style={{ width: 150 }}
                options={AGENT_STATES.map((s) => ({ value: s, label: t(`agentState.${s}`) }))}
              />
              {list.narrowed ? (
                <>
                  <Typography.Text type="secondary">
                    {t('listView.shown', { shown: treeRows.length, total: sourceRows.length })}
                  </Typography.Text>
                  <Button type="link" size="small" onClick={list.reset}>
                    {t('dataTable.clearFilters')}
                  </Button>
                </>
              ) : null}
            </Flex>

            {searchTruncated ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('agents.searchTruncated', { max: searchShown, total: searchTotal })}
                description={t('agents.searchTruncatedHint')}
              />
            ) : null}

            <Table<AgentNode>
              columns={treeColumns}
              /* 56 + 220 + 120 + 130 + 120 + 150 + 170 + 56 = 1022 */
              scroll={{ x: 1030 }}
              dataSource={[...treeRows]}
              rowKey={(row) => row.id}
              loading={roots.isPending || found.isFetching}
              size="small"
              pagination={false}
              onChange={localSortChange<AgentNode>(list.patch)}
              onRow={(row) => ({
                style: { cursor: 'pointer' },
                onClick: () => setSelected(row),
              })}
              rowClassName={(row) => (row.id === selected?.id ? 'ant-table-row-selected' : '')}
              locale={{
                emptyText:
                  // ⚠️ IKKI XIL BO'SHLIK ARALASHTIRILMAYDI: ro'yxatning
                  // o'zi bo'sh bo'lsa «birinchisini qo'shing» deyiladi,
                  // filtr hech narsa topmasa esa filtrni tozalash taklif
                  // etiladi. Bitta matn ikkinchisining o'rniga chiqsa
                  // operator mavjud agentlarni yo'q deb o'ylardi.
                  sourceRows.length > 0 && list.narrowed ? (
                    <EmptyState
                      variant="no-data"
                      title={t('listView.noMatch')}
                      description={t('listView.noMatchHint', { total: sourceRows.length })}
                      actionLabel={t('dataTable.clearFilters')}
                      onAction={list.reset}
                    />
                  ) : (
                    <EmptyState
                      variant="no-data"
                      title={t(isReseller ? 'agents.emptyReseller' : 'agents.emptyAgent')}
                      description={t('agents.emptyHint')}
                    />
                  ),
              }}
            />

            {selected ? (
              <>
                <Typography.Title level={5} style={{ marginTop: 24 }}>
                  {t('agents.childrenOf', { name: selected.displayName })}
                </Typography.Title>
                <Table<AgentNode>
                  columns={treeColumns}
                  /* 56 + 220 + 120 + 130 + 120 + 150 + 170 + 56 = 1022 */
                  scroll={{ x: 1030 }}
                  dataSource={childRows}
                  rowKey={(row) => row.id}
                  loading={children.isPending}
                  size="small"
                  pagination={false}
                  /* ⚠️ Saralash IKKALA jadvalga bir xil qo'llanadi —
                     bitta URL holati, ikkita ko'rinish. Aks holda
                     ota-tugunlar «yangisi birinchi», bolalari esa
                     server tartibida chiqib, ro'yxat ikki xil qonun
                     bo'yicha o'qilardi. */
                  onChange={localSortChange<AgentNode>(list.patch)}
                  onRow={(row) => ({
                    style: { cursor: 'pointer' },
                    onClick: () => setSelected(row),
                  })}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('agents.noChildren')}
                      />
                    ),
                  }}
                />
              </>
            ) : null}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          {selected ? (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card
                title={t('agents.balanceTitle')}
                extra={
                  <Space>
                    <Can permission="agent.deposit.add">
                      <Button size="small" type="primary" onClick={() => setDepositOpen(true)}>
                        {t('agents.deposit')}
                      </Button>
                    </Can>
                    <Can permission="agent.deposit.add">
                      <Button size="small" onClick={() => setLimitOpen(true)}>
                        {t('agents.debtLimit')}
                      </Button>
                    </Can>
                    {fundLimitsShown ? (
                      <Can permission="agent.deposit.add">
                        <Button size="small" onClick={() => setFundLimitOpen(true)}>
                          {t('agents.account.fundLimitTitle')}
                        </Button>
                      </Can>
                    ) : null}
                  </Space>
                }
              >
                {runner.result ? (
                  <div style={{ marginBottom: 16 }}>
                    <OperationResultAlert
                      partialNote={false}
                      result={runner.result}
                      closable
                      onClose={runner.clear}
                    />
                  </div>
                ) : null}

                <Descriptions column={1} size="small" bordered>
                  {/*
                    ⚠️ `terms.*` — TOOLTIP matnlari, yorliq emas
                    (`SessionColumns.tsx` dagi ishlatilishiga qarang).
                    Ilgari bu yerda `terms.balance` turardi va u yorliq
                    o'rniga butun bir jumlani chizardi; ustiga o'sha jumla
                    TRAFIK haqida, bu yerda esa PUL balansi ko'rsatiladi.
                  */}
                  <Descriptions.Item label={t('common.balance')}>
                    <Typography.Text
                      type={(balance.data?.balance.amount ?? 0) < 0 ? 'danger' : undefined}
                    >
                      {formatMoneyValue(balance.data?.balance)}
                    </Typography.Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('agents.available')}>
                    {formatMoneyValue(balance.data?.available)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('agents.debtLimit')}>
                    {/*
                      "Sozlanmagan" va "nol" ATAYLAB farqlanadi: birinchisi
                      limit qo'yilmagan, ikkinchisi ataylab yopilgan. Backend
                      ham shuning uchun `"NOT_SET"` satrini qaytaradi.
                    */}
                    {balance.data?.debtLimit === null
                      ? t('agents.debtLimitNotSet')
                      : formatMoneyValue(balance.data?.debtLimit)}
                  </Descriptions.Item>
                </Descriptions>

                {/*
                  ⚠️ PUL LIMITI QO'YILGANDAN KEYIN KO'RINISHI SHART.
                  Yaratish formasida u so'raladi, ya'ni qiymat bor;
                  ko'rsatadigan joy bo'lmasa operator "qo'ydimmi yoki
                  yo'qmi" degan savolga javob topa olmasdi va limitni
                  qayta yozib, agentni kutilmaganda to'xtatib qo'yardi.

                  ⚠️ `null` va `0` ATAYLAB farqlanadi: birinchisi cheksiz,
                  ikkinchisi "umuman ruxsat yo'q" (`FundLimits` javadoc'i).
                */}
                {fundLimitsShown ? (
                  <Descriptions column={1} size="small" bordered style={{ marginTop: 16 }}>
                    {FUND_LIMIT_KINDS.map((kind) => {
                      const limit = fundLimits.data?.limits[kind] ?? null
                      const used = fundLimits.data?.used[kind] ?? 0
                      /*
                        ⚠️ `PER_TXN` DA «SARFLANDI» YO'Q va bu ataylab
                        (egasi qarori 2026-08-19): u vaqt oynasi emas,
                        bitta to'lovning shifti. «0 / 100 000» deb
                        chizish ekranga yo'q bo'lgan hisobni
                        chiqarardi va operator «bugun hech narsa
                        sarflanmabdi» deb o'qirdi.
                      */
                      const windowed = WINDOWED_FUND_LIMIT_KINDS.includes(kind)
                      return (
                        <Descriptions.Item
                          key={kind}
                          label={t(`agents.account.fundLimitKind.${kind}`)}
                        >
                          {fundLimits.isPending ? (
                            t('common.loading')
                          ) : fundLimits.isError ? (
                            <Typography.Text type="secondary">
                              {translateError(fundLimits.error, t)}
                            </Typography.Text>
                          ) : !windowed ? (
                            limit === null ? (
                              <Tag>{t('agentGovernance.unlimited')}</Tag>
                            ) : (
                              <Space size={4}>
                                <Typography.Text type={limit === 0 ? 'danger' : undefined} strong>
                                  {t('agents.account.perTxnValue', {
                                    limit: formatMoneyValue(money(limit, currency)),
                                  })}
                                </Typography.Text>
                                {limit === 0 ? (
                                  <Tag color="red">{t('agentGovernance.forbidden')}</Tag>
                                ) : null}
                              </Space>
                            )
                          ) : limit === null ? (
                            <Space size={4}>
                              <Tag>{t('agentGovernance.unlimited')}</Tag>
                              <Typography.Text type="secondary">
                                {formatMoneyValue(money(used, currency))}
                              </Typography.Text>
                            </Space>
                          ) : (
                            <Space size={4}>
                              <Typography.Text type={limit === 0 ? 'danger' : undefined} strong>
                                {formatMoneyValue(money(used, currency))} /{' '}
                                {formatMoneyValue(money(limit, currency))}
                              </Typography.Text>
                              {limit === 0 ? (
                                <Tag color="red">{t('agentGovernance.forbidden')}</Tag>
                              ) : null}
                              {limit > 0 && used >= limit ? (
                                <Tag color="orange">{t('agentGovernance.exhausted')}</Tag>
                              ) : null}
                            </Space>
                          )}
                        </Descriptions.Item>
                      )
                    })}
                  </Descriptions>
                ) : null}
              </Card>

              <Card
                title={t('agents.commissionTitle')}
                extra={
                  <Can permission="agent.commission.set">
                    <Button size="small" onClick={() => setCommissionOpen(true)}>
                      {t('agents.commissionSet')}
                    </Button>
                  </Can>
                }
              >
                {/*
                  ⚠️ BEKOR QILISHNING XATOSI SHU YERDA — ilgari u HECH
                  QAYERDA ko'rinmasdi.

                  `closeCommissionRuleMutation` da `onError` ham, xabar
                  ham yo'q edi, holbuki uning ustidagi izohda «xato
                  jimgina yutilmaydi» deb yozilgandi. Server esa
                  haqiqatan rad etadi: qoida allaqachon yopilgan bo'lsa
                  yoki boshqa agentniki bo'lsa — 400 («Ochiq komissiya
                  qoidasi topilmadi»). O'shanda jadval o'zgarmasdi va
                  operator qoida yopilgan deb o'ylab ketardi; farq oy
                  oxirida, komissiya hisoblanganda chiqardi.

                  Kartaning ICHIDA va jadval USTIDA: tasdiq oynasi
                  yopilgan bo'ladi, ya'ni xabarni ko'rsatadigan boshqa
                  yuza qolmaydi.
                */}
                {closeCommissionRuleMutation.isError ? (
                  <Alert
                    type="error"
                    showIcon
                    closable
                    style={{ marginBottom: 16 }}
                    message={translateError(closeCommissionRuleMutation.error, t)}
                    onClose={() => closeCommissionRuleMutation.reset()}
                  />
                ) : null}

                {/* ⚠️ Yuqoridagi `missingGeneralRule` izohiga qarang:
                    jimgina nol — eng yomon shakl. */}
                {missingGeneralRule ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('agents.commissionMissing')}
                    description={t('agents.commissionMissingHint')}
                  />
                ) : null}

                <Table
                  columns={commissionColumns}
                  dataSource={openRules}
                  rowKey={(row) => row.id}
                  loading={rules.isPending}
                  size="small"
                  pagination={false}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('agents.noCommission')}
                      />
                    ),
                  }}
                />
                {closedRuleCount > 0 ? (
                  <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    {t('agents.commissionHistoryNote', { count: closedRuleCount })}
                  </Typography.Text>
                ) : null}

              </Card>

              {/*
                ⚠️ Balans va komissiyaning PASTIDA. Yuqoridagi ikkalasi —
                kundalik ish (pul solish, foiz qo'yish), bu esa kamdan-kam
                qilinadigan, lekin oqibati og'ir amal (agentni to'xtatish).
                Uni tepaga qo'yish "To'xtatish" tugmasini "Depozit"
                tugmasining yoniga olib chiqardi.

                ⚠️ `key` — tanlov o'zgarganda komponent QAYTA quriladi.
                Usiz forma va xato holati eski agentdan yangisiga o'tib
                qolardi: operator A agentning limitlarini ochib, B ga
                bosib, o'sha ochiq formani B ga saqlab yuborishi mumkin edi.
              */}
              {/*
                ⚠️ Abonent amallari chegarasi FAQAT sotuvchi tugunda.
                Toza agentda o'z abonenti yo'q (ADR-029), ya'ni o'chirish
                yoki MAC almashtirish chegarasi unda hech qachon
                sarflanmasdi — egasi uni shuning uchun olib tashlashni
                so'radi. Turi noma'lum bo'lsa (`kind === null`) bo'lim
                QOLADI: bilmagan narsani yashirish ma'lumot yo'qotish
                bo'lardi.
              */}
              <AgentGovernance
                key={selected.id}
                agent={selected}
                showLimits={selected.kind !== 'AGENT'}
              />
            </Space>
          ) : (
            <Card>
              {/* ⚠️ MARSHRUTGA qarab — sahifadagi qolgan hamma matn kabi.
                  Ilgari bu yerda «agentni tanlang» QOTIRIB yozilgan edi
                  va u RESELLERLAR sahifasida ham chiqardi: ekranning
                  o'zi reseller haqida, ko'rsatma esa boshqa turni
                  aytardi. */}
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t(isReseller ? 'agents.selectHintReseller' : 'agents.selectHint')}
              />
            </Card>
          )}
        </Col>
      </Row>

      {/*
        ⚠️ IKKI AMAL, IKKI FORMA — egasi qarori (2026-08-19).

        Oyna bitta, lekin ichidagi savollar to'plami marshrutga qarab
        BUTUNLAY boshqacha: «Agentlar» da kassir ochiladi (login, parol,
        ism, kod, depozit), «Resellerlar» da sotuvchi (shox, turi, o'z
        hisobi). Ilgari ikkalasi ham AYNI to'rt maydonni so'rardi va
        ikkalasiga ham chala mos edi.

        Yaratish MUTATSIYASI bitta va shunday qolishi kerak: uning xatosi
        shu oynada ko'rinadi va yopishda tozalanadi (`tests/guards.mjs`
        naqshi). Ikkinchi mutatsiya ochilsa naqsh ikki joyda
        takrorlanardi.
      */}
      <Modal
        open={createOpen}
        title={t(isReseller ? 'agents.createRootReseller' : 'agents.createRoot')}
        onCancel={closeCreate}
        onOk={() => submitOnValid(createForm, (values) => create.mutate(values))}
        confirmLoading={create.isPending}
        okText={t('operation.create')}
        cancelText={t('common.cancel')}
        /*
          ⚠️ Zinapoya yaroqsiz bo'lsa YARATISH ham to'xtaydi — sabab
          oynaning ichida yozilgan. Aks holda agent yaratilib, komissiya
          400 bilan rad etilardi va operator yarim natijani qo'lda
          tugatishi kerak bo'lardi.
        */
        okButtonProps={{ disabled: createCommission.problems.length > 0 }}
        destroyOnHidden
      >
        {/* "Kod band" (409) SHU YERDA qoladi — kodni tuzatish uchun */}
        {create.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(create.error, t)}
          />
        ) : null}

        <Form
          form={createForm}
          layout="vertical"
          preserve={false}
          disabled={create.isPending}
          /*
            ⚠️ `kind` STANDART qiymati marshrutdan. «Agentlar» da tanlagich
            umuman chizilmaydi (u yerda tanlaydigan narsa yo'q), ya'ni
            forma `AGENT` dan boshqa qiymatni qaytara olmaydi.

            ⚠️ Ota tugun tanlangan qatordan OLDINDAN to'ldiriladi, lekin
            OSHKORA maydon bo'lib qoladi: ilgari u faqat jimgina
            qo'llanardi va operator nima ostida yaratayotganini
            ko'rmasdi.
          */
          /*
            ⚠️ `perTxnLimit` — YAGONA oldindan qo'yilgan pul qiymati.
            100 000 raqamini EGASI berdi (2026-08-19), ya'ni u o'ylab
            topilgan emas. Qolgan pul maydonlari bo'sh boshlanadi:
            taxminiy summa saqlanib qolsa u to'g'ridan-to'g'ri pulga
            ta'sir qilardi.
          */
          initialValues={{
            kind: mode,
            settlementMode: 'DEPOSIT',
            parentId: selected?.id ?? null,
            perTxnLimit: defaultPerTxnLimit(currency),
            // ⚠️ Tur TANLANMAGAN holda boshlanadi: komissiya ixtiyoriy va
            // oldindan «Foiz» qo'yilsa operator uni bilmasdan bo'sh foiz
            // bilan yuborardi.
            commission: COMMISSION_OPTIONAL_INITIAL,
          }}
        >
          {isReseller ? (
            <ResellerCreateFields
              currency={currency}
              canCreateUser={canCreateUser}
              canFund={canFund}
            />
          ) : (
            <AgentCreateFields
              currency={currency}
              canCreateUser={canCreateUser}
              canFund={canFund}
            />
          )}

          {/*
            ⚠️ KOMISSIYA YARATISH FORMASIDA (egasi, 2026-08-20):
            «Agent yaratilganda foizini ham shu yerda sozlab ketish
            kerak, keyin alohida edit qilsa bo'ladi».

            ⚠️ FAQAT KOMISSIYA OLADIGAN turda so'raladi. Toza reseller
            o'z narxi bilan sotadi va unga qoida yozish serverda ham
            ma'nosiz — bo'sh bo'lim «sozlanmagan» degan yolg'on
            taassurot qoldirardi.

            ⚠️ Server BU MA'LUMOTNI ONBOARD SO'ROVIDA QABUL QILMAYDI:
            `AgentOnboardRequest` da komissiya maydoni yo'q. Shuning
            uchun qoida yaratilgandan KEYIN alohida
            `PUT /commission-rules` bilan yoziladi va o'sha so'rov
            yiqilsa natija «yarim bajarildi» ro'yxatida ALOHIDA qator
            bo'lib chiqadi (`createAgentAccountWithCommission`).
          */}
          {commissionOnCreate ? (
            <>
              <Typography.Text strong>{t('agents.commissionTitle')}</Typography.Text>
              <Divider style={{ margin: '8px 0 16px' }} />
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('agents.commissionOptionalOnCreate')}
                description={t('agents.commissionOptionalOnCreateHint')}
              />
              <CommissionFields
                currency={currency}
                state={createCommission}
                perTxnLimitMinor={createPerTxn?.amount ?? null}
                prefix={COMMISSION_PREFIX}
                allowNone
                /* Yangi agentda o'tmish yo'q — qoida darhol kuchga kiradi */
                showValidFrom={false}
              />
            </>
          ) : null}
        </Form>
      </Modal>

      <Modal
        open={depositOpen}
        title={t('agents.depositTitle')}
        onCancel={() => setDepositOpen(false)}
        onOk={() =>
          submitOnValid(depositForm, async (values) => {
            const amount = values.amount ?? money(0, currency)
            const outcome = await runner.run(() =>
              topUpAgent(selected?.id ?? '', amount, depositKey),
            )
            if (outcome.status !== 'FAILED') {
              depositForm.resetFields()
              setDepositOpen(false)
            }
          })
        }
        confirmLoading={runner.running}
        okText={t('billing.topUp.submit')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={depositForm} layout="vertical" preserve={false}>
          <Form.Item
            name="amount"
            label={t('billing.topUp.field')}
            rules={[{ required: true, message: t('billing.topUp.validationAmount') }]}
          >
            <MoneyInput currency={currency} autoFocus />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={limitOpen}
        title={t('agents.debtLimitTitle')}
        onCancel={() => setLimitOpen(false)}
        onOk={() =>
          submitOnValid(limitForm, async (values) => {
            const amount = values.amount ?? money(0, currency)
            const outcome = await runner.run(() => setDebtLimit(selected?.id ?? '', amount))
            if (outcome.status !== 'FAILED') {
              limitForm.resetFields()
              setLimitOpen(false)
            }
          })
        }
        confirmLoading={runner.running}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {/*
          ⚠️ JORIY LIMIT OLDINDAN TO'LDIRILADI (BUG-06).

          Ilgari forma bo'sh ochilardi, holbuki o'ng panelda haqiqiy limit
          KO'RINIB TURARDI — ya'ni ma'lumot qo'l ostida edi, faqat
          ishlatilmasdi. Oqibati jimgina va pulga tegishli: operator
          "limitni 100 000 dan 150 000 ga oshiray" deb kelib, bo'sh
          maydonni ko'rib joriy qiymatni ESLAB qolishga majbur bo'lardi;
          yanglishsa `PUT /debt-limit` ni to'liq ALMASHTIRISH sifatida
          yozadi va agentning krediti jimgina kamayib qolardi.

          `destroyOnHidden` + `preserve={false}` bilan forma har ochilishda
          qaytadan quriladi, ya'ni `initialValues` har safar YANGI balansdan
          o'qiladi — eski qiymat yopishib qolmaydi.

          `null` ("sozlanmagan") va `0` ("ataylab yopilgan") farqi
          saqlanadi: sozlanmagan bo'lsa maydon BO'SH ochiladi, nol emas.
        */}
        <Form
          form={limitForm}
          layout="vertical"
          preserve={false}
          initialValues={{ amount: balance.data?.debtLimit ?? null }}
        >
          <Form.Item
            name="amount"
            label={t('agents.debtLimit')}
            extra={t('agents.debtLimitHint')}
            rules={[{ required: true, message: t('billing.topUp.validationAmount') }]}
          >
            <MoneyInput currency={currency} autoFocus />
          </Form.Item>
        </Form>
      </Modal>

      {/*
        Kunlik va oylik PUL limiti.

        ⚠️ Bu oyna `useOperationRunner` orqali ishlaydi, alohida
        mutatsiya bilan emas — natijani (`FAILED` ham) `runner` o'zi
        `OperationResultAlert` bilan ko'rsatadi va shu bilan
        `tests/guards.mjs` dagi "oyna xatosi oynaning ichida" naqshi
        depozit va qarz limiti oynalari bilan BIR XIL qoladi.

        ⚠️ Server `Map<FundLimitKind, Long>` kutadi — MINOR butun son,
        `Money` ob'ekti EMAS. Aylantirish shu yerda va faqat shu yerda.
      */}
      <Modal
        open={fundLimitOpen}
        title={t('agents.account.fundLimitTitle')}
        onCancel={() => setFundLimitOpen(false)}
        onOk={() =>
          submitOnValid(fundLimitForm, async (values) => {
            const outcome = await runner.run(async () => {
              const saved = await setFundLimits(selected?.id ?? '', {
                DAILY: values.daily?.amount ?? null,
                MONTHLY: values.monthly?.amount ?? null,
                PER_TXN: values.perTxn?.amount ?? null,
              })
              /*
                ⚠️ Xabar SERVER QAYTARGAN qiymatdan quriladi, forma
                kiritganidan emas. Farq bo'lsa (masalan server nolni
                boshqacha o'qisa) operator aynan saqlangan holatni
                ko'radi — "saqladim" degan quruq tasdiq emas.
              */
              return {
                status: 'OK' as const,
                message: t('agents.account.fundLimitSaved', {
                  daily:
                    saved.limits.DAILY === null
                      ? t('agentGovernance.unlimited')
                      : formatMoneyValue(money(saved.limits.DAILY, currency)),
                  monthly:
                    saved.limits.MONTHLY === null
                      ? t('agentGovernance.unlimited')
                      : formatMoneyValue(money(saved.limits.MONTHLY, currency)),
                  perTxn:
                    saved.limits.PER_TXN === null
                      ? t('agentGovernance.unlimited')
                      : formatMoneyValue(money(saved.limits.PER_TXN, currency)),
                }),
              }
            })
            if (outcome.status !== 'FAILED') {
              fundLimitForm.resetFields()
              setFundLimitOpen(false)
            }
          })
        }
        confirmLoading={runner.running}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('agents.account.fundLimitEmptyMeans')}
          description={t('agents.account.fundLimitEmptyMeansHint')}
        />
        {/*
          ⚠️ JORIY QIYMATLAR OLDINDAN TO'LDIRILADI — qarz limiti
          oynasidagi bilan bir xil sabab (BUG-06): bo'sh forma
          "hammasi cheksiz" degan yolg'on boshlang'ich holat bo'lardi
          va saqlash tugmasi mavjud limitlarni JIMGINA o'chirardi.
        */}
        <Form
          form={fundLimitForm}
          layout="vertical"
          preserve={false}
          initialValues={{
            daily:
              fundLimits.data?.limits.DAILY == null
                ? null
                : money(fundLimits.data.limits.DAILY, currency),
            monthly:
              fundLimits.data?.limits.MONTHLY == null
                ? null
                : money(fundLimits.data.limits.MONTHLY, currency),
            perTxn:
              fundLimits.data?.limits.PER_TXN == null
                ? null
                : money(fundLimits.data.limits.PER_TXN, currency),
          }}
        >
          {/* ⚠️ Bitta to'lov limiti BIRINCHI: u har to'ldirishda
              tekshiriladi, kunlik/oylik esa yig'indi bo'yicha. Ikkalasi
              boshqa savolga javob beradi. */}
          <Form.Item
            name="perTxn"
            label={t('agents.account.fundLimitKind.PER_TXN')}
            extra={t('agents.account.perTxnHint')}
          >
            <MoneyInput currency={currency} minMinor={0} autoFocus />
          </Form.Item>
          <Form.Item name="daily" label={t('agents.account.fundLimitKind.DAILY')}>
            <MoneyInput currency={currency} minMinor={0} />
          </Form.Item>
          <Form.Item name="monthly" label={t('agents.account.fundLimitKind.MONTHLY')}>
            <MoneyInput currency={currency} minMinor={0} />
          </Form.Item>
        </Form>
      </Modal>

      {/*
        ⚠️ `key` — tanlov o'zgarganda oyna QAYTA quriladi. Usiz A
        agentning ochilgan formasi B ga o'tib qolardi va saqlash tugmasi
        boshqa tugunga yozardi (`AgentGovernance` bilan bir xil qaror).
      */}
      {selected ? (
        <AgentEditModal
          key={`edit-${selected.id}`}
          open={editOpen}
          node={selected}
          onClose={() => setEditOpen(false)}
        />
      ) : null}

      {selected ? (
        <AgentMoveModal
          key={`move-${selected.id}`}
          open={moveOpen}
          node={selected}
          onClose={() => setMoveOpen(false)}
        />
      ) : null}

      {selected ? (
        <CommissionRuleModal
          key={selected.id}
          open={commissionOpen}
          agentId={selected.id}
          currency={currency}
          perTxnLimitMinor={fundLimits.data?.limits.PER_TXN ?? null}
          onClose={() => setCommissionOpen(false)}
        />
      ) : null}
    </>
  )
}
